import { useEffect, useMemo, useState } from 'react'
import { CLASSES, CLASS_KEYS } from '@shared/rules/constants'
import { api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { SpellCard, levelLabel, type RulesSpell } from '@/components/rules/SpellCard'
import {
  Badge, EmptyState, ErrorNote, Input, PageLoader, Panel, SectionHeading, Select,
} from '@/components/ui'
import { cn } from '@/lib/cn'

type Tab = 'spells' | 'conditions' | 'gear' | 'features'

const TABS: { id: Tab; label: string }[] = [
  { id: 'spells', label: 'Spells' },
  { id: 'conditions', label: 'Conditions' },
  { id: 'gear', label: 'Weapons & gear' },
  { id: 'features', label: 'Features & feats' },
]

const SCHOOLS = [
  'Abjuration', 'Conjuration', 'Divination', 'Enchantment',
  'Evocation', 'Illusion', 'Necromancy', 'Transmutation',
]

export default function Rules() {
  const [tab, setTab] = useState<Tab>('spells')
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  // Search on a short delay so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 220)
    return () => clearTimeout(timer)
  }, [query])

  const search = useAsync(
    () => (debounced.length >= 2 ? api.searchRules(debounced) : Promise.resolve({ hits: [] })),
    [debounced],
  )

  const searching = debounced.length >= 2

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-display">Rules reference</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-400">
          Everything the app knows, in one searchable place — spells, conditions, gear, class
          features, and feats. Tap a spell to see what it actually does.
        </p>
      </header>

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search everything — restrained, fire bolt, greataxe…"
        aria-label="Search the rules"
        className="h-11 text-[15px]"
      />

      {searching ? (
        <SearchResults hits={search.data?.hits ?? []} loading={search.loading} query={debounced} />
      ) : (
        <>
          <nav aria-label="Rules sections" className="-mx-4 overflow-x-auto px-4">
            <div className="flex w-max gap-1 rounded-2xl bg-ink-900/60 p-1">
              {TABS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setTab(entry.id)}
                  aria-current={tab === entry.id ? 'page' : undefined}
                  className={cn(
                    'pressable whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-medium',
                    tab === entry.id
                      ? 'bg-ink-750 text-ink-100 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.06)]'
                      : 'text-ink-400 hover:text-ink-200',
                  )}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </nav>

          {tab === 'spells' && <SpellBrowser />}
          {tab === 'conditions' && <EntryList category="condition" />}
          {tab === 'gear' && <GearList />}
          {tab === 'features' && <EntryList category="class_feature" extra={['species_trait', 'feat']} />}
        </>
      )}
    </div>
  )
}

function SearchResults({
  hits, loading, query,
}: {
  hits: { type: string; id: string; name: string; snippet: string }[]
  loading: boolean
  query: string
}) {
  if (loading) return <PageLoader />
  if (hits.length === 0) {
    return <EmptyState title={`Nothing matches "${query}"`}
      description="Try a shorter word, or part of a spell or condition name." />
  }

  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wider text-ink-500">{hits.length} results</p>
      {hits.map((hit) => (
        <Panel key={`${hit.type}-${hit.id}`} className="flex items-start gap-3 p-3">
          <Badge tone={hit.type === 'spell' ? 'arcane' : hit.type === 'condition' ? 'blood' : 'neutral'}>
            {hit.type.replace('_', ' ')}
          </Badge>
          <div className="min-w-0">
            <p className="text-sm text-ink-100">{hit.name}</p>
            <p className="truncate text-xs text-ink-500">{hit.snippet}</p>
          </div>
        </Panel>
      ))}
    </div>
  )
}

export function SpellBrowser({ action }: { action?: (spell: RulesSpell) => React.ReactNode }) {
  const [level, setLevel] = useState('all')
  const [klass, setKlass] = useState('all')
  const [school, setSchool] = useState('all')

  const { data, error, loading } = useAsync(
    () => api.listSpells({ level, class: klass, school }),
    [level, klass, school],
  )

  const grouped = useMemo(() => {
    const buckets = new Map<number, RulesSpell[]>()
    for (const spell of data?.spells ?? []) {
      const list = buckets.get(spell.level) ?? []
      list.push(spell)
      buckets.set(spell.level, list)
    }
    return [...buckets.entries()].sort((a, b) => a[0] - b[0])
  }, [data])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3">
        <Select value={level} onChange={(e) => setLevel(e.target.value)} aria-label="Spell level" className="w-36">
          <option value="all">Any level</option>
          <option value="0">Cantrips</option>
          <option value="1">1st level</option>
        </Select>
        <Select value={klass} onChange={(e) => setKlass(e.target.value)} aria-label="Class" className="w-40">
          <option value="all">Any class</option>
          {CLASS_KEYS.filter((key) => CLASSES[key].spellcastingAbility).map((key) => (
            <option key={key} value={key}>{CLASSES[key].name}</option>
          ))}
        </Select>
        <Select value={school} onChange={(e) => setSchool(e.target.value)} aria-label="School" className="w-44">
          <option value="all">Any school</option>
          {SCHOOLS.map((value) => <option key={value} value={value}>{value}</option>)}
        </Select>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading ? <PageLoader /> : grouped.length === 0 ? (
        <EmptyState title="No spells match" description="Loosen one of the filters." />
      ) : (
        grouped.map(([spellLevel, spells]) => (
          <section key={spellLevel}>
            <SectionHeading>{levelLabel(spellLevel)} — {spells.length}</SectionHeading>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {spells.map((spell) => (
                <SpellCard key={spell.id} spell={spell} action={action?.(spell)} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  )
}

function EntryList({ category, extra = [] }: { category: string; extra?: string[] }) {
  const { data, error, loading } = useAsync(async () => {
    const all = await Promise.all([category, ...extra].map((c) => api.listRuleEntries(c)))
    return { entries: all.flatMap((r) => r.entries) }
  }, [category, extra.join(',')])

  if (loading) return <PageLoader />
  if (error) return <ErrorNote>{error}</ErrorNote>

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {(data?.entries ?? []).map((entry) => (
        <Panel key={entry.id} className="p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-base">{entry.name}</h3>
            <Badge>{entry.category.replace('_', ' ')}</Badge>
          </div>
          <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-300">{entry.body}</p>
        </Panel>
      ))}
    </div>
  )
}

function GearList() {
  const { data, error, loading } = useAsync(() => api.listRuleItems(), [])
  if (loading) return <PageLoader />
  if (error) return <ErrorNote>{error}</ErrorNote>

  type Item = NonNullable<typeof data>['items'][number]
  const byType = new Map<string, Item[]>()
  for (const item of data?.items ?? []) {
    const list = byType.get(item.type) ?? []
    list.push(item)
    byType.set(item.type, list)
  }

  return (
    <div className="space-y-5">
      {[...byType.entries()].map(([type, items]) => (
        <section key={type}>
          <SectionHeading>{type} — {items.length}</SectionHeading>
          <Panel className="divide-y divide-ink-700/60">
            {items.map((item) => (
              <div key={item.id} className="px-4 py-2.5">
                <p className="text-sm text-ink-100">{item.name}</p>
                <p className="text-xs text-ink-400">{item.description}</p>
              </div>
            ))}
          </Panel>
        </section>
      ))}
    </div>
  )
}
